/**
 * Shared launcher ↔ SPA postMessage protocol — single source of truth
 * imported by both `src/launcher/` and `src/features/app/useEmbedProtocol/`.
 *
 * `ConfigPayload` is the wire `WireChatbotConfig` (from `@circuit/api`) plus
 * an optional `hostOrigin`, with two nullable-field overrides where orval
 * mistyped the BE's actual JSON output.
 */

import type {
  WireChatbotConfig,
  WireChatbotConfigAgent,
  WireChatbotConfigBranding,
} from '@circuit/api';

import {
  DEFAULT_HIDE_ATTRIBUTION,
  DEFAULT_OPEN_BY_DEFAULT,
  DEFAULT_PLACEMENT,
  DEFAULT_STYLE,
} from '@circuit/chat/widget-chrome/tokens';

export const PROTOCOL_VERSION = 1;

/** Sentinel passed as `targetOrigin` to `postMessage` to accept any origin. */
export const ANY_ORIGIN = '*';

export interface EmbedMessage<
  TType extends string = string,
  TPayload = unknown,
> {
  payload: TPayload;
  type: TType;
  version: number;
}

// ----- Launcher → SPA -----

/**
 * `ConfigPayload` is the wire `WireChatbotConfig` plus a client-side
 * `hostOrigin`. Two fields are narrowed against the wire because the BE
 * actually emits JSON `null` for unset values — swag→orval drops the
 * nullable bit and types them as plain `string`.
 */
type AgentWithNullableWelcome = Omit<
  WireChatbotConfigAgent,
  'welcomeMessage'
> & {
  welcomeMessage: string | null;
};
type BrandingWithNullableLogo = Omit<WireChatbotConfigBranding, 'logo'> & {
  logo: string | null;
};

export type ConfigPayload = Omit<WireChatbotConfig, 'agent' | 'branding'> & {
  agent: AgentWithNullableWelcome;
  branding: BrandingWithNullableLogo;
  /** Host page's `window.location.origin`; injected by the launcher. */
  hostOrigin?: string;
};

export type EmptyPayload = Record<string, never>;

export type LauncherToSpaType = 'CONFIG' | 'WIDGET_CLOSE' | 'WIDGET_OPEN';

// ----- SPA → Launcher -----

/** `placement: 'corner'` pins to `branding.position`; `'center'` viewport-centers. */
export interface SetSizePayload {
  height: number;
  placement?: 'center' | 'corner';
  width: number;
}

export interface HeightChangePayload {
  height: number;
}

export interface BadgeCountPayload {
  count: number;
}

export interface FullscreenRequestPayload {
  enabled: boolean;
}

export type SpaToLauncherType =
  | 'BADGE_COUNT'
  | 'CLOSE_REQUEST'
  | 'FULLSCREEN_REQUEST'
  | 'HEIGHT_CHANGE'
  | 'SET_SIZE'
  | 'WIDGET_READY';

// ----- Concrete message types -----

export type ConfigMessage = EmbedMessage<'CONFIG', ConfigPayload>;
export type WidgetOpenMessage = EmbedMessage<'WIDGET_OPEN', EmptyPayload>;
export type WidgetCloseMessage = EmbedMessage<'WIDGET_CLOSE', EmptyPayload>;
export type WidgetReadyMessage = EmbedMessage<'WIDGET_READY', EmptyPayload>;
export type CloseRequestMessage = EmbedMessage<'CLOSE_REQUEST', EmptyPayload>;
export type SetSizeMessage = EmbedMessage<'SET_SIZE', SetSizePayload>;
export type HeightChangeMessage = EmbedMessage<
  'HEIGHT_CHANGE',
  HeightChangePayload
>;
export type BadgeCountMessage = EmbedMessage<'BADGE_COUNT', BadgeCountPayload>;
export type FullscreenRequestMessage = EmbedMessage<
  'FULLSCREEN_REQUEST',
  FullscreenRequestPayload
>;

export interface SpaToLauncherPayloadMap {
  BADGE_COUNT: BadgeCountPayload;
  CLOSE_REQUEST: EmptyPayload;
  FULLSCREEN_REQUEST: FullscreenRequestPayload;
  HEIGHT_CHANGE: HeightChangePayload;
  SET_SIZE: SetSizePayload;
  WIDGET_READY: EmptyPayload;
}
export interface LauncherToSpaPayloadMap {
  CONFIG: ConfigPayload;
  WIDGET_CLOSE: EmptyPayload;
  WIDGET_OPEN: EmptyPayload;
}

/**
 * Presentation style, mirrored from `branding.style`.
 * - `windowed`: opaque bordered pane with a modal shadow (classic drawer).
 * - `floating`: transparent iframe hosting individually-floating cards
 *   (header + chat body fused, prompt separate) that hover over the host page.
 *
 * BE emits the raw wire string, which may be missing/unknown on old keys;
 * `normalizeChatStyle` collapses that to the two valid variants and defaults
 * to `windowed`.
 */
export type ChatStyle = 'floating' | 'windowed';

export function normalizeChatStyle(value: string | undefined): ChatStyle {
  return value === 'floating' ? 'floating' : 'windowed';
}

// ----- Defaults -----

/** Reference/test fixture shape only — the launcher does NOT fall back to
 *  this on `/chat-bot/config` failure (failure = no bubble). Kept exported
 *  so tests and demos can share one canonical shape. */
export const DEFAULT_CONFIG: ConfigPayload = {
  agent: { name: 'Circuit', welcomeMessage: null },
  features: { actions: false, askUserQuestion: false },
  branding: {
    hideAttribution: DEFAULT_HIDE_ATTRIBUTION,
    logo: null,
    openByDefault: DEFAULT_OPEN_BY_DEFAULT,
    position: DEFAULT_PLACEMENT,
    primaryColor: '#30a46c',
    style: DEFAULT_STYLE,
  },
};

// ----- Type guards -----

function isEnvelope(value: unknown): value is Partial<EmbedMessage> {
  if (typeof value !== 'object' || value === null) return false;
  const message = value as Partial<EmbedMessage>;
  return (
    message.version === PROTOCOL_VERSION && typeof message.type === 'string'
  );
}

export function isConfigMessage(value: unknown): value is ConfigMessage {
  if (!isEnvelope(value) || value.type !== 'CONFIG') return false;
  const payload = value.payload as Partial<ConfigPayload> | undefined;
  if (typeof payload?.agent?.name !== 'string') return false;
  if (typeof payload.branding?.primaryColor !== 'string') return false;
  return typeof payload.branding.position === 'string';
}

export function isWidgetOpenMessage(
  value: unknown,
): value is WidgetOpenMessage {
  return isEnvelope(value) && value.type === 'WIDGET_OPEN';
}

export function isWidgetCloseMessage(
  value: unknown,
): value is WidgetCloseMessage {
  return isEnvelope(value) && value.type === 'WIDGET_CLOSE';
}

export function isWidgetReadyMessage(
  value: unknown,
): value is WidgetReadyMessage {
  return isEnvelope(value) && value.type === 'WIDGET_READY';
}

export function isCloseRequestMessage(
  value: unknown,
): value is CloseRequestMessage {
  return isEnvelope(value) && value.type === 'CLOSE_REQUEST';
}

export function isSetSizeMessage(value: unknown): value is SetSizeMessage {
  if (!isEnvelope(value) || value.type !== 'SET_SIZE') return false;
  const payload = value.payload as Partial<SetSizePayload> | undefined;
  if (typeof payload?.width !== 'number' || typeof payload.height !== 'number')
    return false;
  return !Number.isNaN(payload.width) && !Number.isNaN(payload.height);
}

export function isHeightChangeMessage(
  value: unknown,
): value is HeightChangeMessage {
  if (!isEnvelope(value) || value.type !== 'HEIGHT_CHANGE') return false;
  const payload = value.payload as Partial<HeightChangePayload> | undefined;
  return typeof payload?.height === 'number' && !Number.isNaN(payload.height);
}

export function isBadgeCountMessage(
  value: unknown,
): value is BadgeCountMessage {
  if (!isEnvelope(value) || value.type !== 'BADGE_COUNT') return false;
  const payload = value.payload as Partial<BadgeCountPayload> | undefined;
  return typeof payload?.count === 'number' && !Number.isNaN(payload.count);
}

export function isFullscreenRequestMessage(
  value: unknown,
): value is FullscreenRequestMessage {
  if (!isEnvelope(value) || value.type !== 'FULLSCREEN_REQUEST') return false;
  const payload = value.payload as
    | Partial<FullscreenRequestPayload>
    | undefined;
  return typeof payload?.enabled === 'boolean';
}
